/**
 * FieldTypeRegistry - Extensible Field Configuration System
 * 
 * SOLID Principles: Open/Closed Principle (OCP) & Single Responsibility Principle (SRP)
 * Purpose: This registry system allows new sensor field types to be added without
 * modifying existing code. It centralizes all field-specific configuration including
 * units, formatting rules, and display properties.
 * 
 * Responsibilities:
 * - Map field names to appropriate units
 * - Define formatting rules for different field types
 * - Provide extensible configuration for new sensor types
 * - Abstract field-specific logic from visualization components
 * 
 * Context: Buoy sensors measure diverse parameters (temperature, pressure, CO2, etc.)
 * Each type needs specific units and formatting. New sensor types are frequently added.
 */

import { LabelUtils } from './LabelUtils.js';

/**
 * Base configuration interface for field types
 * All field type configurations should implement this structure
 */
export class FieldType {
  constructor(config) {
    this.units = config.units || '';
    this.formatValue = config.formatValue || this._defaultFormatter;
    this.precision = config.precision || 3;
    this.category = config.category || 'general';
    this.description = config.description || '';
  }

  /**
   * Default value formatter - can be overridden by specific field types
   * @param {number} value - Raw sensor value
   * @returns {string} Formatted value with units
   */
  _defaultFormatter(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return 'No data';
    }
    return `${Number(value).toFixed(this.precision)} ${this.units}`;
  }
}

/**
 * Registry of all field type configurations
 * Each key represents a field name pattern, value is the FieldType configuration
 */
const FIELD_TYPE_REGISTRY = {
  // === CO2 Sensor Field Types ===
  // ProCV CO2 sensor - measures partial pressure of CO2 in seawater
  'pCO2': new FieldType({
    units: 'μatm',
    precision: 2,
    category: 'carbon_chemistry',
    description: 'Partial pressure of CO2 in seawater',
    formatValue: function(value, metadata = {}) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'No data';
      }
      
      // Special handling for ProCV sensor mode information
      const mode = metadata.mode;
      const modeLabel = mode === "A M" || mode === "a_m" ? " (Air Mode)" : 
                       mode === "W M" || mode === "w_m" ? " (Water Mode)" : 
                       mode ? ` (${mode})` : "";
      
      return `${Number(value).toFixed(this.precision)} ${this.units}${modeLabel}`;
    }
  }),

  'calculated_co2_ppmv': new FieldType({
    units: 'ppmv',
    precision: 2,
    category: 'carbon_chemistry',
    description: 'Calculated CO2 concentration'
  }),

  'corrected_co2_ppmv': new FieldType({
    units: 'ppmv',
    precision: 2,
    category: 'carbon_chemistry',
    description: 'Temperature/pressure corrected CO2 concentration'
  }),

  'raw_co2_ppm': new FieldType({
    units: 'ppm',
    precision: 2,
    category: 'carbon_chemistry',
    description: 'Raw CO2 sensor reading'
  }),

  // === Temperature Field Types ===
  // Various temperature sensors across different instruments
  'temperature_c': new FieldType({
    units: '°C',
    precision: 2,
    category: 'temperature',
    description: 'Water temperature'
  }),

  'sensor_temperature_c': new FieldType({
    units: '°C',
    precision: 2,
    category: 'temperature',
    description: 'Sensor internal temperature'
  }),

  'air_temp_c': new FieldType({
    units: '°C',
    precision: 2,
    category: 'temperature',
    description: 'Air temperature'
  }),

  'sea_surface_temp_c': new FieldType({
    units: '°C',
    precision: 2,
    category: 'temperature',
    description: 'Sea surface temperature'
  }),

  'temperature_deg_c': new FieldType({
    units: '°C',
    precision: 2,
    category: 'temperature',
    description: 'Water temperature'
  }),

  // === CTD (Conductivity, Temperature, Depth) Sensor Types ===
  'conductivity_s_per_m': new FieldType({
    units: 'S/m',
    precision: 4,
    category: 'oceanography',
    description: 'Electrical conductivity of seawater'
  }),

  'conductivity_s_m': new FieldType({
    units: 'S/m',
    precision: 4,
    category: 'oceanography',
    description: 'Electrical conductivity of seawater'
  }),

  'specific_conductivity_s_per_m': new FieldType({
    units: 'S/m',
    precision: 4,
    category: 'oceanography',
    description: 'Temperature-compensated conductivity'
  }),

  'pressure_dbar': new FieldType({
    units: 'dbar',
    precision: 2,
    category: 'oceanography',
    description: 'Water pressure (depth indicator)'
  }),

  'salinity_psu': new FieldType({
    units: 'PSU',
    precision: 3,
    category: 'oceanography',
    description: 'Practical salinity units'
  }),

  'oxygen_ml_per_l': new FieldType({
    units: 'ml/L',
    precision: 2,
    category: 'oceanography',
    description: 'Dissolved oxygen concentration'
  }),

  'oxygen_mg_l': new FieldType({
    units: 'mg/L',
    precision: 2,
    category: 'oceanography',
    description: 'Dissolved oxygen concentration'
  }),

  'sound_velocity_m_per_s': new FieldType({
    units: 'm/s',
    precision: 1,
    category: 'oceanography',
    description: 'Speed of sound in water'
  }),

  'sound_velocity_m_s': new FieldType({
    units: 'm/s',
    precision: 1,
    category: 'oceanography',
    description: 'Speed of sound in water'
  }),

  // === Pressure Field Types ===
  'gas_pressure_mbar': new FieldType({
    units: 'mbar',
    precision: 2,
    category: 'pressure',
    description: 'Gas pressure measurement'
  }),

  'msl_pressure_hpa': new FieldType({
    units: 'hPa',
    precision: 2,
    category: 'pressure',
    description: 'Mean sea level atmospheric pressure'
  }),

  // === Virtual Wave Field Types ===
  // Computed from wave spectrum analysis
  'hs_m': new FieldType({
    units: 'm',
    precision: 2,
    category: 'waves',
    description: 'Significant wave height'
  }),

  'hmax_m': new FieldType({
    units: 'm',
    precision: 2,
    category: 'waves',
    description: 'Maximum wave height'
  }),

  'tmax_s': new FieldType({
    units: 's',
    precision: 1,
    category: 'waves',
    description: 'Period of maximum wave'
  }),

  'dir_mean_deg': new FieldType({
    units: '°',
    precision: 0,
    category: 'directional',
    description: 'Mean wave direction'
  }),

  'spread_deg': new FieldType({
    units: '°',
    precision: 1,
    category: 'waves',
    description: 'Wave directional spread'
  }),

  // === Virtual Wind Field Types ===
  'wind_speed_avg_mps': new FieldType({
    units: 'm/s',
    precision: 2,
    category: 'wind',
    description: 'Average wind speed'
  }),

  'wind_speed_max_mps': new FieldType({
    units: 'm/s',
    precision: 2,
    category: 'wind',
    description: 'Maximum wind speed'
  }),

  'wind_dir_avg_deg': new FieldType({
    units: '°',
    precision: 0,
    category: 'directional',
    description: 'Average wind direction'
  }),

  // === Virtual Current Field Types ===
  'speed_mps': new FieldType({
    units: 'm/s',
    precision: 3,
    category: 'currents',
    description: 'Current speed'
  }),

  'dir_deg': new FieldType({
    units: '°',
    precision: 0,
    category: 'directional',
    description: 'Current direction'
  }),

  // === Virtual Tide Field Types ===
  'water_level_m': new FieldType({
    units: 'm',
    precision: 3,
    category: 'tides',
    description: 'Water level above datum'
  }),

  'prediction_m': new FieldType({
    units: 'm',
    precision: 3,
    category: 'tides',
    description: 'Predicted tide level'
  }),

  // === System Field Types ===
  'supply_voltage_v': new FieldType({
    units: 'V',
    precision: 2,
    category: 'system',
    description: 'Power supply voltage'
  }),

  'epoch_time': new FieldType({
    units: 's',
    precision: 0,
    category: 'system',
    description: 'Unix timestamp'
  }),

  // === Count/Raw Data Types ===
  'reference_ad_counts': new FieldType({
    units: 'counts',
    precision: 0,
    category: 'raw_data',
    description: 'Reference A/D converter counts'
  }),

  'current_ad_counts': new FieldType({
    units: 'counts',
    precision: 0,
    category: 'raw_data',
    description: 'Current A/D converter counts'
  }),

  'current_counts': new FieldType({
    units: 'counts',
    precision: 0,
    category: 'raw_data',
    description: 'Current sensor counts'
  })
};

/**
 * Main registry class for managing field type configurations
 */
export class FieldTypeRegistry {
  /**
   * Gets the field type configuration for a given field key
   * 
   * @param {string} fieldKey - Format: 'measurement:field', 'buoyId_measurement:field', or just 'field'
   * @returns {FieldType} Field type configuration
   */
  static getFieldType(fieldKey) {
    // Handle buoy-prefixed field keys by extracting the base field key first
    const baseFieldKey = LabelUtils.getBaseFieldKey(fieldKey);
    
    // Extract field name from measurement:field format
    const fieldName = baseFieldKey.includes(':') ? baseFieldKey.split(':')[1] : baseFieldKey;
    
    // First check for exact field name match
    if (FIELD_TYPE_REGISTRY[fieldName]) {
      return FIELD_TYPE_REGISTRY[fieldName];
    }
    
    // Check for pattern matches (e.g., fields with units in brackets)
    const fieldWithoutUnits = fieldName.replace(/\[.*?\]/g, '');
    if (FIELD_TYPE_REGISTRY[fieldWithoutUnits]) {
      return FIELD_TYPE_REGISTRY[fieldWithoutUnits];
    }
    
    // Try to extract units from field name itself (e.g., 'conductivity[S/m]')
    const unitMatch = fieldName.match(/\[([^\]]+)\]/);
    if (unitMatch) {
      return new FieldType({
        units: unitMatch[1],
        description: `${fieldWithoutUnits} measurement`
      });
    }
    
    // Return default field type for unknown fields
    return new FieldType({
      units: '',
      description: `${fieldName} measurement`
    });
  }

  /**
   * Gets units for a specific field
   * 
   * @param {string} fieldKey - Field identifier
   * @returns {string} Units string
   */
  static getUnits(fieldKey) {
    return this.getFieldType(fieldKey).units;
  }

  /**
   * Formats a value according to its field type configuration
   * 
   * @param {number} value - Raw sensor value
   * @param {string} fieldKey - Field identifier
   * @param {Object} metadata - Additional data (e.g., sensor mode)
   * @returns {string} Formatted value string
   */
  static formatValue(value, fieldKey, metadata = {}) {
    const fieldType = this.getFieldType(fieldKey);
    return fieldType.formatValue(value, metadata);
  }

  /**
   * Registers a new field type (extends the system without modifying existing code)
   * 
   * @param {string} fieldName - Field identifier
   * @param {FieldType} fieldType - Field type configuration
   */
  static registerFieldType(fieldName, fieldType) {
    FIELD_TYPE_REGISTRY[fieldName] = fieldType;
  }

  /**
   * Gets all field types in a specific category
   * 
   * @param {string} category - Category name ('temperature', 'pressure', etc.)
   * @returns {Array} Array of field types in category
   */
  static getFieldsByCategory(category) {
    return Object.entries(FIELD_TYPE_REGISTRY)
      .filter(([_, fieldType]) => fieldType.category === category)
      .map(([fieldName, fieldType]) => ({ fieldName, fieldType }));
  }

  /**
   * Gets all available categories
   * 
   * @returns {Array} Array of category names
   */
  static getCategories() {
    const categories = new Set();
    Object.values(FIELD_TYPE_REGISTRY).forEach(fieldType => {
      categories.add(fieldType.category);
    });
    return Array.from(categories).sort();
  }

  /**
   * Checks if a field requires special rendering (like pCO2 with mode dots)
   * 
   * @param {string} fieldKey - Field identifier
   * @returns {boolean} True if field needs special rendering
   */
  static requiresSpecialRendering(fieldKey) {
    return fieldKey === 'procv_co2:pCO2' || fieldKey === 'mini_co2:pCO2';
  }

  /**
   * Gets special color for a field (consolidated from ColorUtils)
   * 
   * @param {string} fieldKey - Field identifier
   * @returns {string|null} Special color or null if none
   */
  static getSpecialColor(fieldKey) {
    if (fieldKey === 'procv_co2:pCO2' || fieldKey === 'mini_co2:pCO2') {
      return '#6c757d'; // Grey for pCO2 lines (mode shown via dot color)
    }
    return null;
  }
}

export default FieldTypeRegistry;